import {
  AiOutboundPhoneCallNodeData,
  OrganizationService,
  PhoneCallCommunicationPlatform,
  PhoneCallCommunicationPlatformConfig,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { FormattedNode } from '../../WorkflowBuilder';
import {
  useAutoSaveDebouncedCallback,
  useNodeConfigurationEventListener,
} from '../utils/nodeConfigurationUtils';
import { TestNodeDrawer } from './TestNodeDrawer';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const googleOAuthClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
console.log('google client id', googleOAuthClientId);

export default function AiOutboundPhoneCallConfiguration({
  selectedNode,
  setNodes,
  readOnly = false,
  setAllRequiredFieldsOfNodeConfigurationFilled,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  readOnly?: boolean;
  setAllRequiredFieldsOfNodeConfigurationFilled: Dispatch<
    SetStateAction<boolean>
  >;
}) {
  const wF = useStore($workflow);
  const org = useStore($org);
  const nodes = useNodes();
  const queryClient = useQueryClient();
  const [testDrawerOpen, setTestDrawerOpen] = useState(false);


  const {
    data: orgPhoneNumbers,
    error,
    isLoading: loadingOrgPhoneNumbers,
    isFetching: fetchingOrgPhoneNumbers,
    refetch,
  } = useQuery({
    queryKey: ['orgPhoneNumbers', org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      if (!org?.id) return [];
      const res = await OrganizationService.getOrganizationPhoneNumbers(
        org?.id || '',
      );
      return res;
    },
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });


  // Keep config as it is
  const [communicationPlatformConfig, setCommunicationPlatformConfig] =
    useState<PhoneCallCommunicationPlatformConfig | {}>({
      accountSid: '',
      authToken: '',
      phoneNumber: '+18035908513',
    });
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [toPhoneNumber, setToPhoneNumber] = useState<string>('+917086721479');
  const [llmModel, setLlmModel] = useState<string>('xdfs');
  const [communicationPlatform, setCommunicationPlatform] =
    useState<PhoneCallCommunicationPlatform>('TWILIO');

  function isAiOutboundPhoneCallNodeData(
    data: any,
  ): data is AiOutboundPhoneCallNodeData {
    return data.type === 'AI_OUTBOUND_PHONE_CALL';
  }

  async function saveConfigurations() {
    if (!readOnly) {
      if (Object.keys(communicationPlatformConfig).length === 0) {
        toast.error('Please select the communication platform!');
        return;
      }

      console.log('llmModel', llmModel);
      if (!toPhoneNumber) {
        toast.error('Please enter the phone number of the recepient!');
        return;
      }
      if (!aiPrompt) {
        toast.error('Please enter the phone call instructions!');
        return;
      }
      if (!llmModel) {
        toast.error('Please select an LLM model!');
        return;
      }
      if (!communicationPlatform) {
        toast.error('Please select a communication platform!');
        return;
      }
      console.log('llmModel', llmModel);
      $savingNodeConfiguration.set(true);
      setAllRequiredFieldsOfNodeConfigurationFilled(true);
      const updatableNode = updateNodeLocally();
      if (updatableNode) {
        await updateNode.mutateAsync({
          req: {
            data: { ...updatableNode.data },
          },
          nodeId: updatableNode?.id,
        });
      }
      $savingNodeConfiguration.set(false);
    }
  }

  useAutoSaveDebouncedCallback(
    async () => {
      await saveConfigurations();
    },
    2000, // 1 seconds delay
    [communicationPlatformConfig, aiPrompt, toPhoneNumber, llmModel] as const,
  );

  useNodeConfigurationEventListener('confirmSaveChanges', async detail => {
    await saveConfigurations();
  });

  function updateNodeLocally() {
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode: FormattedNode = newNodes.find(
      (n: any) => n.id === selectedNode.id,
    );

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      updatableNode.data = {
        ...updatableNode.data,
        type: 'AI_OUTBOUND_PHONE_CALL' as WorkflowNodeType,
      };
      if (isAiOutboundPhoneCallNodeData(updatableNode.data)) {
        if (communicationPlatformConfig) {
          updatableNode.data = {
            ...updatableNode.data,
            communication_platform_config:
              communicationPlatformConfig as PhoneCallCommunicationPlatformConfig,
          };
        }

        if (toPhoneNumber) {
          updatableNode.data = {
            ...updatableNode.data,
            phone_number_to: toPhoneNumber,
          };
        }
        if (aiPrompt) {
          updatableNode.data = {
            ...updatableNode.data,
            ai_prompt: aiPrompt,
          };
        }
        if (llmModel) {
          updatableNode.data = {
            ...updatableNode.data,
            llm_model: llmModel,
          };
        }
        if (communicationPlatform) {
          updatableNode.data = {
            ...updatableNode.data,
            communication_platform: communicationPlatform,
          };
        }
      }
      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;
      setNodes(newNodes);
      return updatableNode;
    }
  }

  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      console.log('payload', payload);
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Configurations updated!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  useEffect(() => {
    if (selectedNode) {
      if (isAiOutboundPhoneCallNodeData(selectedNode.data)) {
        setCommunicationPlatform(
          selectedNode.data.communication_platform ?? 'TWILIO',
        );
        setCommunicationPlatformConfig(
          selectedNode.data.communication_platform_config ?? {},
        );
        setToPhoneNumber(selectedNode.data.phone_number_to ?? '');
        setLlmModel(selectedNode.data.llm_model ?? 'xdfs'); // xdfs @TODO: Need to fix this later, we are putting a dummy value now becayse backend is not using llm_model anywhere
        setAiPrompt(selectedNode.data.ai_prompt ?? '');
      }
    }
  }, [selectedNode]);

  // ---------------------------------------------------------------------
  // UI / JSX
  // ---------------------------------------------------------------------
  return (
    <div className="flex h-auto w-full flex-col bg-gray-50">
      <div className="flex-1 overflow-y-auto">
        <div className="m-auto mx-auto max-w-sm bg-white p-4 shadow-sm">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
            </label>
            <div className="">
            <label
              htmlFor="to"
              className="flex items-center text-sm font-medium text-gray-700"
            >
              From{' '}
              {(loadingOrgPhoneNumbers || fetchingOrgPhoneNumbers) && (
                <Loader2 className="ml-2 h-3 w-3 animate-spin" />
              )}
            </label>

            <div className="mt-1 rounded-md bg-gray-100 p-2 text-sm">
              {/* @TODO: Here we are showing the first number in the list assuming that
              there will be only one number per org for now, later on we have to improve this
              to show the number in  more robust way */}
              {orgPhoneNumbers &&
                orgPhoneNumbers?.length > 0 &&
                orgPhoneNumbers[0]?.phoneNumber}
            </div>
          </div>

            {/* <Select
              onValueChange={value => {
                if (!readOnly) {
                  // update the data ingestion node configuration
                  setCommunicationPlatform(
                    value as PhoneCallCommunicationPlatform,
                  );
                }
              }}
              value={communicationPlatform as string} // @NOTE: Need to fix the types later on
              disabled={readOnly}
            >
              <SelectTrigger
                className={`w-[100%] ${readOnly ? 'cursor-not-allowed bg-gray-200' : ''}`}
              >
                <SelectValue placeholder="Select a data source" />
              </SelectTrigger>
              <SelectContent className="w-[100%]">
                <SelectGroup>
                  <SelectLabel>Sources</SelectLabel>
                  <SelectItem value="TWILIO">Twilio</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select> */}
            {/* <Button
                    variant="default"
                    className="text-sm"
                    onClick={handleConnectNewAccount}
                  >
                    Connect
                  </Button> */}
          </div>

          {/* To Field (local state) */}
          <div className="mt-4">
            <label
              htmlFor="prompt"
              className="block text-sm font-medium text-gray-700"
            >
              Prompt
            </label>
            <textarea
              id="prompt"
              name="prompt"
              value={aiPrompt}
              onChange={e => {
                if (!readOnly) {
                  setAiPrompt(e.target.value);
                }
              }}
              className={`mt-1 block w-full rounded-md border border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''
              }`}
              rows={8}
              placeholder="Enter AI prompt here..."
              disabled={readOnly}
            />
          </div>
          <p className="text-sm text-gray-500">
            Variables: [company_name], [customer_name], [invoice_name],
            [phone_number], [email], [street_address], [city], [state],
            [zip_code], [country], [invoice_amount], [invoice_paid_amount],
            [invoice_balance], [invoice_due_date], [invoice_invoice_date],
            [payment_link]
          </p>
          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={readOnly || updateNode.isPending} // ✅ Prevent save when read-only
              onClick={async () => {
                setTestDrawerOpen(true);
              }}
              className={`px-3 py-1 text-sm${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
            >
              Test
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={readOnly}
                    onClick={async () => {
                      await saveConfigurations();
                    }}
                    className={`px-3 py-1 text-sm ${readOnly ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    {updateNode.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Changes are automatically saved after editing</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <TestNodeDrawer
        open={testDrawerOpen}
        onClose={() => setTestDrawerOpen(false)}
        node={selectedNode}
        workflow={wF}
      />
        </div>
      </div>
    </div>
  );
}
